import z from 'zod'
import { IncidentReportForm } from '../pages/Dashboard/schools/components/ReportIncidentModal'
import { CallableFunctionResponse } from './helpers'
import { ReportIssueForm } from '../pages/Dashboard/schools/components/ReportIssueModal'
import { ChipLabelMap, ChipMap } from './types-school'

export type SchoolBooking = {
    teacherDetails?: BookingTeacherDetails
    replacementDetails?: BookingReplacementDetails
    placementDetails?: BookingPlacementDetails
    schoolDetails?: BookingSchoolDetails
    schoolNotes?: BookingSchoolNotes
    schoolFeedback?: Record<string, SchoolFeedback>
    teacherFeedback?: TeacherFeedback

    supportDetails?: Partial<BookingSupportDetails>

    schoolIssues?: Record<string, ReportIssueForm>
    schoolIncident?: Record<string, IncidentReport>
}

export type BookingSupportDetails = {
    supervisionEngagementSupport: ChipMap<ES_SES_key>
    communicationInteractionSupport: ChipMap<ES_CIS_key>
    regulationWellbeingSupport: ChipMap<ES_RWS_key>
}

export const ES_SES_map: ChipLabelMap = {
    oneToOne: '1:1 support required',
    smallGroup: 'Small group support (2-4 students)',
    recessLunch: 'Support during recess/lunch',
    transitions: 'Support during transitions',
    classroomOnly: 'Support limited to classroom setting',
} as const
type ES_SES_key = keyof typeof ES_SES_map

export const ES_CIS_map: ChipLabelMap = {
    altComms: 'May use alternative communication methods',
    simplifiedInstructions: 'Requires simplified instructions',
    repeatedPrompts: 'Requires repeated prompts/redirection',
    nonVerbal: 'Communication may be primarily non-verbal',
    peerGuidance: 'Guidance required during peer interactions',
}
type ES_CIS_key = keyof typeof ES_CIS_map

export const ES_RWS_map: ChipLabelMap = {
    emotionalRegulation: 'Support with emotional regulation',
    movementBreaks: 'May require movement breaks',
    quietSpace: 'May require quiet/reset space',
    behaviourPlan: 'Behaviour support plan in place',
}
type ES_RWS_key = keyof typeof ES_RWS_map

export const supportDetailsLabelMap: Record<
    keyof BookingSupportDetails,
    ChipLabelMap
> = {
    supervisionEngagementSupport: ES_SES_map,
    communicationInteractionSupport: ES_CIS_map,
    regulationWellbeingSupport: ES_RWS_map,
} as const

export type IssueReport = ReportIssueForm & {
    raisedBy: string
    raisedByName: string
    timeStamp: string
}
export type IncidentReport = Omit<IncidentReportForm, 'time'> & {
    timeStampReported: number
    time: string
}

export const schoolBookingKeyNames = [
    'teacherDetails',
    'replacementDetails',
    'placementDetails',
    'schoolDetails',
    'schoolNotes',
    'schoolFeedback',
    'teacherFeedback',
    'schoolIssues',
    'schoolIncident',
] as (keyof SchoolBooking)[]

export type SchoolFeedback = {
    dailyPlan?: boolean
    onTime?: boolean
    feedbackForTeacher: string
    timeStamp: number
    userName?: string
}
export type TeacherFeedback = {
    promptedToComplete: number
    schoolFeedback: string
    timeStamp: number
    schoolUsersNotified: { [userId: string]: number }
}
export type BookingSchoolNotes = {
    notesForTeacher?: string
    replacingTeacherNamed?: string
    subjectReplacing?: string
}

export const BookingTerms = {
    termOne: 'termOne',
    termTwo: 'termTwo',
    termThree: 'termThree',
    termFour: 'termFour',
} as const
export type BookingTerm = (typeof BookingTerms)[keyof typeof BookingTerms]
export const BookingTermOrder: Record<BookingTerm, number> = {
    termOne: 1,
    termTwo: 2,
    termThree: 3,
    termFour: 4,
} as const

export const isBookingTermBefore = (
    left: BookingTerm,
    right: BookingTerm
): boolean => BookingTermOrder[left] < BookingTermOrder[right]

export const BookingTermsLabelMap: Record<BookingTerm, string> = {
    termOne: 'Term 1',
    termTwo: 'Term 2',
    termThree: 'Term 3',
    termFour: 'Term 4',
} as const

export type BookingPlacementDetails = {
    autoReplace?: boolean
    term?: BookingTerm
    type?: SchoolBookingType
    placementType?: 'Full Day'
    startTime?: string
    finishTime?: string
    state?: PlacementDetailsState

    timeStampConfirmedBySchool?: string
    schoolCancelTimeStamp?: string
}
export const PlacementDetailsStates = {
    confirmed: 'confirmed',
    cancelledBySchool: 'cancelledBySchool',
    cancelledByTeacher: 'cancelledByTeacher',
} as const
export type PlacementDetailsState =
    (typeof PlacementDetailsStates)[keyof typeof PlacementDetailsStates]

export const BookingTeacherStates = {
    cancelled: 'cancelled',
    confirmed: 'confirmed',
    enRoute: 'enRoute',
    nearby: 'nearby',
    inProgress: 'inProgress',
    earlyCheckout: 'earlyCheckout',
    completed: 'completed',
} as const
export type BookingTeacherState =
    (typeof BookingTeacherStates)[keyof typeof BookingTeacherStates]
export type BookingTeacherDetails = {
    state: BookingTeacherState
    profileImageURL?: string
    firstName?: string
    lastName?: string
    vitReference?: string
    schoolCancelReason?: string
}

export const BookingReplacementStates = {
    closed: 'closed',
    inProgress: 'inProgress',
    complete: 'complete',
} as const
export type BookingReplacementState =
    (typeof BookingReplacementStates)[keyof typeof BookingReplacementStates]

export type BookingReplacementDetails = {
    state: BookingReplacementState
}

export type viewedUserList = Record<string, string>

export type SchoolBookingKey = {
    placementUid: string
    cancelled?: boolean
    notified?: boolean // old strucutre
    cancelledByTeacher?: boolean
    notifiedOfCancellation?: boolean
    // Users that have viewed the cancellation notification on mobile
    cancelNotification?: viewedUserList
    // Auto replacement
    claimedByTeacher?: boolean
    // Users that have viewed the replacement confirmation notification on mobile
    replacementNotification?: viewedUserList
    replaced?: string // placementUid that this booking is replacing for
    esBooking?: boolean
}
export type SchoolBookingKeys = {
    [date: string]: {
        [teacherUid: string]: SchoolBookingKey
    }
}
export type SchoolBookingKeysForDate = {
    [teacherUid: string]: SchoolBookingKey
}

export const SchoolBookingTypes = {
    Primary: 'Primary',
    Secondary: 'Secondary',
} as const
export type SchoolBookingType =
    (typeof SchoolBookingTypes)[keyof typeof SchoolBookingTypes]

export type BookingSchoolDetails = {
    schoolLatitude?: number
    schoolLongitude?: number
    schoolLogoUrl?: string
    schoolName?: string
    schoolUid?: string

    teacherCancelReason?: string
    sparkCancelReason?: string
    campus?: string
}

export const reinstateBookingProps = z.object({
    country: z.string(),
    locationState: z.string(),
    date: z.string(),
    placementUid: z.string(),
    isSupportStaff: z.boolean().optional(),
})
export type ReinstateBookingProps = z.infer<typeof reinstateBookingProps>
export type ReinstateBookingResponse = CallableFunctionResponse<{}>
